import type { CarUpgrade, RenderMode, VehicleBlueprint, WeaponUpgrade } from "@neon-arena/shared";
import { ClientNetwork } from "../networking/ClientNetwork.js";
import type { Game } from "../game/Game.js";
import type { ControlMethod } from "./VehicleSelect.js";
import {
  QuickMatchMachine,
  bigDisplayFor,
  headerFor,
  overlayVisible,
  renderRoster,
  renderStandings,
  statusLineFor,
  subDisplayFor,
  winnerNameOf,
  escapeHtml,
} from "./quickMatchState.js";
import { announcer, type AnnouncerLineId } from "./announcer.js";
import { matchAnnouncer } from "./announcerMatch.js";

/** Everything quickmatch needs. All of it comes from the lobby's existing state — it asks the player nothing. */
export interface QuickMatchOptions {
  serverUrl: string;
  driverName: string;
  blueprint: VehicleBlueprint;
  upgrades: CarUpgrade;
  weaponUpgrades: WeaponUpgrade;
  controlMethod: ControlMethod;
  renderMode: RenderMode;
  authToken: string | null;
}

export interface QuickMatchDeps {
  game: Game;
  /** Back to the lobby — the caller re-shows whatever screen it came from. */
  onExit: () => void;
  /** Surfaced through the existing `#start-error` element. */
  onError: (message: string) => void;
}

/** UI clock. 4 Hz is plenty for second-resolution countdowns and costs nothing. */
const TICK_MS = 250;

/**
 * QUICK MATCH — the client for the always-on queue room
 * (`server/src/rooms/QueueArenaRoom.ts`, docs/menu-redesign-plan.md §3.4).
 *
 * ── Deferred spawn, and why it looks like this ──────────────────────────────
 * The queue room does NOT spawn you on join, and it does not spawn you on
 * `hello` either — `handleHello` waitlists you and holds your payload. So the
 * client's job is: connect, send `hello` (that is what puts you IN the queue —
 * a client that never says hello is not counted as a human and will not even
 * arm the countdown), then WAIT. The local `Game` is not constructed until the
 * server has sent BOTH `seat {seated:true}` and the round's `start`.
 *
 * Everything below the round boundary is the existing game: once booted, the
 * `Game` adopts this already-connected `ClientNetwork` (`MatchStartOptions.network`)
 * and the in-game HUD takes over. This class never touches HUD internals.
 */
export class QuickMatchScreen {
  private readonly machine = new QuickMatchMachine();
  private net: ClientNetwork | null = null;
  private root: HTMLElement | null = null;
  private tick: ReturnType<typeof setInterval> | null = null;
  private opts: QuickMatchOptions | null = null;
  private wired = false;
  /**
   * `Game.start()` builds the scene, physics and viewmodel — it is a one-shot.
   * A player who is evicted and later re-seated must NOT re-run it: from the
   * running Game's point of view a re-seat is just another round reset (the
   * server re-spawns our PlayerState and `applyServerForLocal` picks it back
   * up), exactly like the existing `round-reset` path.
   */
  private gameBooted = false;
  private leaveWired = false;
  /**
   * Countdown 3/2/1 are SCHEDULED off the known end-time, not sampled on the
   * 250 ms UI tick — sampling made the spoken number land up to a tick late and
   * unevenly. `countdownArmedFor` is the phaseEndsAt we armed for (0 = none).
   */
  private countdownArmedFor = 0;
  private countdownTimers: ReturnType<typeof setTimeout>[] = [];

  private headerEl!: HTMLElement;
  private statusEl!: HTMLElement;
  private bigEl!: HTMLElement;
  private subEl!: HTMLElement;
  private bannerEl!: HTMLElement;
  private standingsEl!: HTMLElement;
  private rosterEl!: HTMLElement;

  constructor(private readonly deps: QuickMatchDeps) {}

  // ── Lifecycle ───────────────────────────────────────────────────────────────

  async start(opts: QuickMatchOptions): Promise<void> {
    this.opts = opts;
    this.gameBooted = false;
    this.machine.reset();
    this.mount();
    this.show();
    this.render();

    const net = new ClientNetwork();
    this.net = net;
    this.wireHandlers(net);

    try {
      // Join the queue room WITHOUT spawning. `connect()` never sends `hello`.
      await net.connect({
        serverUrl: opts.serverUrl,
        driverName: opts.driverName,
        target: "queue",
        authToken: opts.authToken,
      });
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      this.machine.error(msg);
      this.render();
      this.deps.onError(`QUICK MATCH FAILED — ${msg}`);
      this.teardownNetwork();
      this.hide();
      this.deps.onExit();
      return;
    }

    // `hello` = "put me in the queue". Same `driverName` as the joinOrCreate
    // options above, or the room-level and player-level names diverge
    // (lobby-upgrade-plan §8b gotcha 5).
    net.enterMatch({
      driverName: opts.driverName,
      blueprint: opts.blueprint,
      weaponUpgrades: opts.weaponUpgrades,
    });
    this.machine.connected();
    this.render();
  }

  /**
   * Clean room leave, from any phase.
   *
   * Once the Game has booted we take the same exit the in-game LEAVE button
   * has always taken (`destroy()` + reload): `Game` has no partial teardown that
   * returns it to a re-startable state (`resetForRetry()` drops the room, which
   * is exactly what we must not do while still queued). Before the boot, leaving
   * is just a room leave and the lobby comes back instantly.
   */
  leave(): void {
    this.stopTick();
    this.clearCountdown();
    this.teardownNetwork();
    this.hide();
    this.machine.reset();
    if (this.gameBooted) {
      this.deps.game.destroy();
      window.location.reload();
      return;
    }
    this.deps.onExit();
  }

  private teardownNetwork(): void {
    try { this.net?.leave(); } catch { /* already gone */ }
    this.net = null;
  }

  // ── Server → model ──────────────────────────────────────────────────────────

  private wireHandlers(net: ClientNetwork): void {
    net.onQueueState((p) => { this.machine.queueState(p); this.afterMessage(); });
    net.onQueuePos((p) => { this.machine.queuePos(p); this.afterMessage(); });
    net.onSeat((p) => {
      const wasSeated = this.machine.model().seated;
      this.machine.seat(p);
      // Announcer: the seat grant IS "match found" (not on a re-broadcast).
      if (p.seated && !wasSeated) announcer.announce("match_found");
      this.afterMessage();
    });
    net.onRoundStart((p) => {
      this.machine.start(p);
      if (this.machine.model().seated) {
        // Round boundary for the match tracker (arms time_60/time_10) …
        matchAnnouncer.roundStarted(p.config ? Date.now() + p.config.durationS * 1000 : 0);
        // … and the start pair: GO (high) with ROUND START queued behind it.
        announcer.announce("go");
        announcer.announce("round_start");
      }
      this.afterMessage();
    });
    net.onRoundEnd((p) => {
      this.machine.roundEnd(p);
      matchAnnouncer.roundEnded();
      // Only for rounds we actually played (standings carry every seated human).
      const ownId = net.ownId();
      const standings = p.standings ?? [];
      if (ownId && standings.some((s) => s.sessionId === ownId)) {
        announcer.announce("round_end");
        const won = standings[0]?.sessionId === ownId;
        // Sequenced, not interrupted: let the short ROUND END clip land first.
        setTimeout(() => announcer.announce(won ? "victory" : "winner"), 1400);
      }
      this.afterMessage();
    });
  }

  private afterMessage(): void {
    if (this.machine.shouldEnterMatch()) void this.enterMatch();
    this.render();
  }

  /**
   * The seat grant landed and a round we're in has started. Boot the game ONCE;
   * on later rounds (winner-stays-on) the server just resets the arena around
   * the already-running `Game`, and a re-seat after eviction is picked up by
   * `Game.applyServerForLocal` when our `PlayerState` reappears — neither needs
   * a second boot, which `markInGame()` + the null-on-roundEnd config guarantees.
   */
  private async enterMatch(): Promise<void> {
    const opts = this.opts;
    const net = this.net;
    if (!opts || !net) return;
    this.machine.markInGame();
    this.render();
    // Re-seated after an eviction: the Game is already live and already bound to
    // this room. Nothing to boot — just drop the overlay.
    if (this.gameBooted) return;
    this.gameBooted = true;
    try {
      this.deps.game.setRenderMode(opts.renderMode);
      await this.deps.game.start({
        network: net, // ← adopt the queue room; do NOT connect/hello again
        serverUrl: opts.serverUrl,
        driverName: opts.driverName,
        blueprint: opts.blueprint,
        controlMethod: opts.controlMethod,
        upgrades: opts.upgrades,
        weaponUpgrades: opts.weaponUpgrades,
        authToken: opts.authToken,
      });
      // The overlay steps aside for a live round, so LEAVE has to live somewhere
      // else while playing: reuse the HUD's existing (hidden) leave button —
      // markup only, no HUD behaviour touched.
      this.revealInGameLeave();
      this.render();
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      console.error("[quickmatch] game boot failed:", err);
      // Undo the partial start so LEAVE returns to the lobby rather than
      // reloading the page. `resetForRetry` also drops the room — which is the
      // right thing here: a Game that can't build is a Game that can't play.
      this.gameBooted = false;
      try { this.deps.game.resetForRetry(); } catch (e) { console.error("[quickmatch] resetForRetry failed:", e); }
      this.machine.error(msg);
      this.render();
      this.deps.onError(`QUICK MATCH FAILED — ${msg}`);
      this.leave();
    }
  }

  /** Show the HUD's leave button and point it at our clean-leave path. */
  private revealInGameLeave(): void {
    const btn = document.getElementById("leave-btn");
    if (!btn || this.leaveWired) return;
    this.leaveWired = true;
    btn.classList.remove("hidden");
    btn.addEventListener("click", () => this.leave());
  }

  // ── DOM ─────────────────────────────────────────────────────────────────────

  private mount(): void {
    if (this.root) return;
    const root = document.createElement("div");
    root.id = "quickmatch-screen";
    root.className = "hidden";
    root.innerHTML = `
      <div class="qm-panel">
        <div class="qm-title">QUICK MATCH</div>
        <div class="qm-header" id="qm-header">CONNECTING</div>
        <div class="qm-banner hidden" id="qm-banner"></div>
        <div class="qm-big" id="qm-big">···</div>
        <div class="qm-sub" id="qm-sub"></div>
        <div class="qm-status" id="qm-status">FINDING A GAME…</div>
        <div class="qm-standings" id="qm-standings"></div>
        <div class="qm-roster" id="qm-roster"></div>
        <button class="qm-leave" id="qm-leave-btn">LEAVE</button>
      </div>`;
    document.body.appendChild(root);
    this.root = root;
    this.headerEl = root.querySelector("#qm-header")!;
    this.statusEl = root.querySelector("#qm-status")!;
    this.bigEl = root.querySelector("#qm-big")!;
    this.subEl = root.querySelector("#qm-sub")!;
    this.bannerEl = root.querySelector("#qm-banner")!;
    this.standingsEl = root.querySelector("#qm-standings")!;
    this.rosterEl = root.querySelector("#qm-roster")!;

    if (!this.wired) {
      this.wired = true;
      root.querySelector("#qm-leave-btn")!.addEventListener("click", () => this.leave());
    }
  }

  private show(): void {
    this.root?.classList.remove("hidden");
    this.startTick();
  }

  private hide(): void {
    this.root?.classList.add("hidden");
    this.stopTick();
  }

  private startTick(): void {
    if (this.tick !== null) return;
    this.tick = setInterval(() => this.render(), TICK_MS);
  }

  private stopTick(): void {
    if (this.tick !== null) { clearInterval(this.tick); this.tick = null; }
  }

  /** Schedule countdown_3/2/1 to fire exactly at 3 s / 2 s / 1 s remaining. */
  private armCountdown(phaseEndsAt: number, now: number): void {
    this.clearCountdown();
    this.countdownArmedFor = phaseEndsAt;
    for (const d of [3, 2, 1]) {
      const delay = phaseEndsAt - d * 1000 - now;
      // Fire slightly-late digits immediately, but never a stale one already
      // past its own second (a late "3" is worse than silence — a mid-countdown
      // joiner just picks up from the digit that is still ahead).
      if (delay < -250) continue;
      const line = `countdown_${d}` as AnnouncerLineId;
      this.countdownTimers.push(setTimeout(() => announcer.announce(line), Math.max(0, delay)));
    }
  }

  private clearCountdown(): void {
    for (const t of this.countdownTimers) clearTimeout(t);
    this.countdownTimers.length = 0;
    this.countdownArmedFor = 0;
  }

  private render(): void {
    if (!this.root) return;
    const m = this.machine.model();

    // The overlay hides itself only for a round we're actually playing.
    const visible = overlayVisible(m);
    this.root.classList.toggle("hidden", !visible);
    if (!visible) { this.stopTick(); return; }
    this.startTick();

    const now = Date.now();

    // Announcer: arm 3/2/1 as precise timers off the countdown end-time once,
    // when we enter (or the server re-times) the countdown. setTimeout lands the
    // spoken number on the second boundary regardless of the coarse UI tick.
    if (m.phase === "countdown" && m.seated && m.phaseEndsAt > 0) {
      if (this.countdownArmedFor !== m.phaseEndsAt) this.armCountdown(m.phaseEndsAt, now);
    } else if (this.countdownArmedFor !== 0) {
      this.clearCountdown();
    }

    this.headerEl.textContent = headerFor(m);
    this.bigEl.textContent = bigDisplayFor(m, now);
    this.subEl.textContent = subDisplayFor(m, now);
    this.statusEl.textContent = statusLineFor(m);
    this.root.dataset["phase"] = m.phase;

    const winner = m.phase === "intermission" ? winnerNameOf(m.standings) : "";
    this.bannerEl.classList.toggle("hidden", !winner);
    if (winner) this.bannerEl.innerHTML = `WINNER — ${escapeHtml(winner)} <span class="qm-wso">STAYS ON</span>`;

    const ownId = this.net?.ownId() ?? "";
    this.standingsEl.innerHTML = m.standings.length > 0 ? renderStandings(m.standings, ownId) : "";
    this.rosterEl.innerHTML = m.phase === "match" && !m.seated ? renderRoster(m.roster) : "";
  }
}
